import { getErrorMessage, handleApiError } from "../utils/axios-error";
import axiosClient from "../utils/fetchAxiosClient";
import type {
  OnboardingData,
  OnboardingRequestResponse,
  OnboardingStatusResponse,
  WorkdayConfig
} from "./tenant.types";

export interface CopyTenantRequest {
  clientName: string;
  workdayConfig: WorkdayConfig;
  uuid: string; // Required: UUID to look up onboarding record and determine target environment
  /**
   * @deprecated IGNORED by backend. The target environment is derived from the
   * stored onboarding record associated with the UUID. This field is kept for
   * backward compatibility but will be ignored.
   */
  targetEnvironment?: string;
}

export interface UpdateOnboardingStageRequest {
  uuid: string;
  stage:
    | "WORKDAY_CONFIGURED"
    | "DOMAIN_CONFIGURED"
    | "SSO_CONFIGURED"
    | "IMPL_COMPLETED";
  tenantId?: string;
  onboardingData?: OnboardingData;
}

export const tenantService = {
  /**
   * Copies tenant0 document to create a new tenant.
   *
   * SECURITY: The backend derives the target environment from the stored onboarding
   * record associated with the UUID. Any targetEnvironment in the request body is IGNORED.
   */
  async copyTenant0(data: CopyTenantRequest) {
    try {
      const response = await axiosClient.post(
        "/tenant-provision/copy-tenant0",
        data
      );
      return response.data;
    } catch (error: unknown) {
      throw handleApiError(error);
    }
  },

  /**
   * Updates the onboarding stage for a client.
   *
   * ARCHITECTURE: The backend always writes to rooster-impl (central source of truth).
   * Environment detection is handled by hub-api based on the stored onboarding record.
   */
  async updateOnboardingStage(
    clientId: string,
    data: UpdateOnboardingStageRequest
  ) {
    try {
      const response = await axiosClient.put(
        `/tenant-provision/${clientId}/onboarding-stage`,
        data
      );
      return response.data;
    } catch (error: unknown) {
      throw handleApiError(error);
    }
  },

  /**
   * Gets the onboarding status for a specific UUID.
   * Returns status, tenantId, onboardingData, and computed environment details.
   *
   * ARCHITECTURE: The backend always reads from rooster-impl (central source of truth).
   * Environment detection is handled by hub-api based on the stored onboarding record.
   * The response includes the authoritative environment in the `environment` field.
   */
  async getOnboardingStatus(uuid: string): Promise<OnboardingStatusResponse> {
    try {
      const response = await axiosClient.get(
        `/tenant-provision/onboarding-status/${uuid}`
      );
      return response.data;
    } catch (error: unknown) {
      throw handleApiError(error);
    }
  },

  /**
   * Mark a specific stage as complete in the sequenced onboarding workflow.
   * This triggers the backend to:
   * - Record the completion timestamp and user
   * - Update the onboarding request status
   * - Send emails to the next role(s) if workday stage is complete
   */
  async markStageComplete(
    uuid: string,
    stage: "workday" | "domain" | "sso",
    completedBy: string
  ) {
    try {
      const response = await axiosClient.post(
        `/client-onboarding/${uuid}/stage/${stage}/complete`,
        { completedBy }
      );
      return response.data;
    } catch (error: unknown) {
      throw handleApiError(error);
    }
  },

  /**
   * Get the onboarding request details including stage completion status
   */
  async getOnboardingRequest(uuid: string): Promise<OnboardingRequestResponse> {
    try {
      const response = await axiosClient.get(`/client-onboarding/${uuid}`);
      return response.data;
    } catch (error: unknown) {
      throw handleApiError(error);
    }
  },

  /**
   * Provision production, sandbox, and preview tenants from an implementation tenant
   */
  async provisionProductionTenants(data: {
    uuid: string;
    sourceTenantId: string;
    productionTenantId: string;
    sandboxTenantId: string;
    previewTenantId: string;
  }) {
    try {
      const response = await axiosClient.post(
        "/tenant-provision/provision-production-tenants",
        data
      );
      return response.data;
    } catch (error: unknown) {
      throw handleApiError(error);
    }
  },

  /**
   * Save instructions data (workday_region, zoom_connected) to MongoDB.
   * Called when clicking "Next: Workday Configuration" on the instructions page.
   *
   * ARCHITECTURE: This saves to MongoDB so hub-api calculates the correct target
   * environment, eliminating the need for sessionStorage-based environment detection.
   */
  async saveInstructionsData(
    uuid: string,
    data: {
      workdayRegion: "US" | "EU";
      zoomConnected: "Yes" | "No";
    }
  ) {
    try {
      const response = await axiosClient.put(
        `/client-onboarding/${uuid}/instructions`,
        data
      );
      return response.data;
    } catch (error: unknown) {
      throw handleApiError(error);
    }
  },

  /**
   * Get navigation context for backend-governed onboarding UI
   */
  async getNavigationContext(uuid: string) {
    try {
      const response = await axiosClient.get(
        `/client-onboarding/${uuid}/navigation-context`
      );
      return response.data;
    } catch (error: unknown) {
      console.error(
        "Error getting navigation context:",
        getErrorMessage(error)
      );
      throw handleApiError(error);
    }
  }
};
